import { useEffect, useRef, useState } from 'react'
import type { ChangeEvent } from 'react'
import * as Popover from '@radix-ui/react-popover'
import { Check, Pencil, Plus, Sparkles, Trash2, X } from 'lucide-react'
import { api } from '../lib/api'
import type { GeneratedTemplate } from '../lib/api'
import type { Project, ReplyTemplate } from '../lib/types'
import { Button, IconButton, Input, Select, Spinner, Textarea } from '../ds'
import { errorText, useToast } from './Toaster'
import { useConfirm } from './Confirm'

interface Candidate extends GeneratedTemplate {
  selected: boolean
}

const COUNT_OPTIONS = [3, 4, 5, 6]
const MAX_REPLY_EXAMPLES = 12

// Автоматически растущая по содержимому строка ввода без видимой рамки — как
// редактируемая ячейка в Notion (рамка/фон проявляются на hover/focus).
function GrowField({
  value,
  placeholder,
  autoFocus,
  onChange,
  onBlur
}: {
  value: string
  placeholder?: string
  autoFocus?: boolean
  onChange: (value: string) => void
  onBlur?: () => void
}) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const resize = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }
  useEffect(() => {
    if (ref.current) resize(ref.current)
  }, [value])
  return (
    <textarea
      ref={ref}
      className="tpl-inline"
      rows={1}
      value={value}
      placeholder={placeholder}
      autoFocus={autoFocus}
      onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
      onInput={(e) => resize(e.currentTarget)}
      onBlur={onBlur}
    />
  )
}

export function TemplatesSettings({
  projectId,
  examples,
  onProjectSaved
}: {
  projectId: string
  examples: string[]
  onProjectSaved: (project: Project) => void
}) {
  const { notify } = useToast()
  const confirm = useConfirm()

  const [exampleList, setExampleList] = useState<string[]>(examples.length ? examples : [''])
  const savedExamples = useRef(JSON.stringify(examples))
  const [savedFlash, setSavedFlash] = useState(false)

  const [templates, setTemplates] = useState<ReplyTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)

  // Генерация ИИ (в поповере).
  const [genOpen, setGenOpen] = useState(false)
  const [instruction, setInstruction] = useState('')
  const [count, setCount] = useState(5)
  const [generating, setGenerating] = useState(false)
  const [candidates, setCandidates] = useState<Candidate[] | null>(null)

  // Ручное добавление (composer по кнопке).
  const [composerOpen, setComposerOpen] = useState(false)
  const [manualTitle, setManualTitle] = useState('')
  const [manualBody, setManualBody] = useState('')
  const [adding, setAdding] = useState(false)

  useEffect(() => {
    api
      .listReplyTemplates(projectId)
      .then((res) => setTemplates(res.templates))
      .catch((err) => notify(errorText(err), 'error'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId])

  // Сохраняем примеры, только если что-то реально изменилось (тихо, по blur).
  async function persistExamples(list: string[]): Promise<boolean> {
    const cleaned = list.map((e) => e.trim()).filter(Boolean)
    if (JSON.stringify(cleaned) === savedExamples.current) return true
    try {
      const res = await api.updateProject(projectId, { replyExamples: cleaned })
      savedExamples.current = JSON.stringify(res.project.replyExamples)
      setExampleList(res.project.replyExamples.length ? res.project.replyExamples : [''])
      onProjectSaved(res.project)
      setSavedFlash(true)
      setTimeout(() => setSavedFlash(false), 1600)
      return true
    } catch (err) {
      notify(errorText(err), 'error')
      return false
    }
  }

  function removeExample(index: number) {
    const next = exampleList.length === 1 ? [''] : exampleList.filter((_, i) => i !== index)
    setExampleList(next)
    persistExamples(next)
  }

  async function generate() {
    if (!(await persistExamples(exampleList))) return
    setGenerating(true)
    try {
      const res = await api.generateReplyTemplates(projectId, { count, instruction: instruction.trim() })
      setCandidates(res.candidates.map((c) => ({ ...c, selected: true })))
      setGenOpen(false)
    } catch (err) {
      notify(errorText(err), 'error')
    } finally {
      setGenerating(false)
    }
  }

  async function saveCandidates() {
    const chosen = (candidates || []).filter((c) => c.selected && c.body.trim())
    if (chosen.length === 0) {
      notify('Выберите хотя бы один шаблон', 'error')
      return
    }
    try {
      const res = await api.createReplyTemplates(projectId, {
        source: 'ai',
        items: chosen.map((c) => ({ title: c.title, body: c.body }))
      })
      setTemplates((prev) => [...prev, ...res.templates])
      setCandidates(null)
      notify(`Добавлено шаблонов: ${res.templates.length}`)
    } catch (err) {
      notify(errorText(err), 'error')
    }
  }

  async function addManual() {
    if (!manualBody.trim()) return
    setAdding(true)
    try {
      const res = await api.createReplyTemplates(projectId, { title: manualTitle.trim(), body: manualBody.trim() })
      setTemplates((prev) => [...prev, ...res.templates])
      setManualTitle('')
      setManualBody('')
      setComposerOpen(false)
    } catch (err) {
      notify(errorText(err), 'error')
    } finally {
      setAdding(false)
    }
  }

  async function removeTemplate(template: ReplyTemplate) {
    if (!template.id) return
    const ok = await confirm({ title: 'Удалить шаблон?', message: `«${template.title}» будет удалён безвозвратно.` })
    if (!ok) return
    try {
      await api.deleteReplyTemplate(template.id)
      setTemplates((prev) => prev.filter((t) => t.id !== template.id))
    } catch (err) {
      notify(errorText(err), 'error')
    }
  }

  async function saveEdit(template: ReplyTemplate, title: string, bodyText: string) {
    if (!template.id) return
    if (!bodyText.trim()) {
      notify('Текст шаблона обязателен', 'error')
      return
    }
    try {
      const res = await api.updateReplyTemplate(template.id, { title: title.trim(), body: bodyText.trim() })
      setTemplates((prev) => prev.map((t) => (t.id === template.id ? res.template : t)))
      setEditingId(null)
    } catch (err) {
      notify(errorText(err), 'error')
    }
  }

  return (
    <div className="tpl">
      {/* --- Примеры стиля --- */}
      <section className="tpl-sec">
        <div className="tpl-sec-head">
          <span className="section-label">
            Примеры стиля · {exampleList.filter((example) => example.trim()).length}/{MAX_REPLY_EXAMPLES}
          </span>
          {savedFlash && (
            <span className="tpl-saved">
              <Check size={12} /> Сохранено
            </span>
          )}
        </div>
        <p className="tpl-note">Живые ответы вашей команды — ИИ переймёт тон и манеру.</p>
        <div className="tpl-examples">
          {exampleList.map((ex, i) => (
            <div key={i} className="tpl-ex">
              <GrowField
                value={ex}
                placeholder={`Пример ответа №${i + 1}`}
                onChange={(v) => setExampleList((prev) => prev.map((p, idx) => (idx === i ? v : p)))}
                onBlur={() => persistExamples(exampleList)}
              />
              <button type="button" className="tpl-ex-del" aria-label="Удалить пример" onClick={() => removeExample(i)}>
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="tpl-addline"
          disabled={exampleList.length >= MAX_REPLY_EXAMPLES}
          title={exampleList.length >= MAX_REPLY_EXAMPLES ? `Максимум ${MAX_REPLY_EXAMPLES} примеров` : undefined}
          onClick={() => setExampleList((prev) => (prev.length >= MAX_REPLY_EXAMPLES ? prev : [...prev, '']))}
        >
          <Plus size={14} /> Добавить пример
        </button>
      </section>

      {/* --- Шаблоны --- */}
      <section className="tpl-sec">
        <div className="tpl-sec-head">
          <span className="section-label">Шаблоны{templates.length ? ` · ${templates.length}` : ''}</span>
          <div className="tpl-sec-actions">
            <Popover.Root open={genOpen} onOpenChange={setGenOpen}>
              <Popover.Trigger className="btn btn-ghost btn-sm">
                <Sparkles size={14} /> Сгенерировать
              </Popover.Trigger>
              <Popover.Portal>
                <Popover.Content className="tpl-gen-pop" align="end" sideOffset={6}>
                  <p className="tpl-note">
                    ИИ предложит варианты на основе tone of voice и примеров. Сохраните те, что понравятся.
                  </p>
                  <Input
                    value={instruction}
                    placeholder="Пожелание (необязательно)"
                    onChange={(e) => setInstruction(e.target.value)}
                  />
                  <div className="row spread">
                    <label className="row" style={{ gap: 6, fontSize: 'var(--fs-13)' }}>
                      Сколько:
                      <Select
                        value={String(count)}
                        onValueChange={(v) => setCount(Number(v))}
                        aria-label="Сколько шаблонов сгенерировать"
                        options={COUNT_OPTIONS.map((n) => ({ value: String(n), label: String(n) }))}
                      />
                    </label>
                    <Button size="sm" onClick={generate} disabled={generating}>
                      <Sparkles size={14} /> {generating ? 'Генерируем…' : 'Сгенерировать'}
                    </Button>
                  </div>
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>
            <Button variant="secondary" size="sm" onClick={() => setComposerOpen((v) => !v)}>
              <Plus size={14} /> Добавить
            </Button>
          </div>
        </div>

        {/* Предпросмотр ИИ-кандидатов */}
        {candidates && (
          <div className="tpl-cands">
            <div className="tpl-cands-head">
              <span>Выбрано {candidates.filter((c) => c.selected).length} из {candidates.length}</span>
              <IconButton label="Скрыть" onClick={() => setCandidates(null)}>
                <X size={14} />
              </IconButton>
            </div>
            {candidates.map((c, i) => (
              <div key={i} className={['tpl-cand', c.selected ? '' : 'off'].filter(Boolean).join(' ')}>
                <button
                  type="button"
                  className={['tpl-pick', c.selected ? 'on' : ''].filter(Boolean).join(' ')}
                  aria-label={c.selected ? 'Не сохранять' : 'Сохранить'}
                  onClick={() => setCandidates((prev) => prev!.map((v, idx) => (idx === i ? { ...v, selected: !v.selected } : v)))}
                >
                  {c.selected ? <Check size={12} /> : null}
                </button>
                <div className="tpl-cand-fields">
                  <Input
                    value={c.title}
                    placeholder="Название"
                    onChange={(e) => setCandidates((prev) => prev!.map((v, idx) => (idx === i ? { ...v, title: e.target.value } : v)))}
                  />
                  <Textarea
                    value={c.body}
                    rows={2}
                    onChange={(e) => setCandidates((prev) => prev!.map((v, idx) => (idx === i ? { ...v, body: e.target.value } : v)))}
                  />
                </div>
              </div>
            ))}
            <div className="row" style={{ gap: 8 }}>
              <Button size="sm" onClick={saveCandidates}>
                Добавить выбранные ({candidates.filter((c) => c.selected).length})
              </Button>
              <Button variant="ghost" size="sm" onClick={generate} disabled={generating}>
                Ещё варианты
              </Button>
            </div>
          </div>
        )}

        {/* Ручной composer */}
        {composerOpen && (
          <div className="tpl-edit">
            <Input value={manualTitle} placeholder="Название (необязательно)" onChange={(e) => setManualTitle(e.target.value)} />
            <Textarea value={manualBody} rows={3} placeholder="Текст шаблона ответа…" autoFocus onChange={(e) => setManualBody(e.target.value)} />
            <div className="tpl-edit-actions">
              <Button variant="ghost" size="sm" onClick={() => setComposerOpen(false)}>
                Отмена
              </Button>
              <Button size="sm" onClick={addManual} disabled={adding || !manualBody.trim()}>
                Добавить
              </Button>
            </div>
          </div>
        )}

        {/* Список */}
        {loading ? (
          <div className="row" style={{ justifyContent: 'center', padding: 16 }}>
            <Spinner />
          </div>
        ) : templates.length === 0 && !composerOpen ? (
          <p className="tpl-note tpl-empty">Пока пусто. Сгенерируйте с ИИ или добавьте вручную.</p>
        ) : (
          <div className="tpl-list">
            {templates.map((t) =>
              editingId === t.id ? (
                <TemplateEditor key={t.id} template={t} onSave={saveEdit} onCancel={() => setEditingId(null)} />
              ) : (
                <div key={t.id} className="tpl-item" onClick={() => setEditingId(t.id)}>
                  <div className="tpl-item-text">
                    <span className="tpl-item-title">
                      {t.title}
                      {t.source === 'ai' && <span className="tpl-src">ИИ</span>}
                    </span>
                    <span className="tpl-item-body">{t.body}</span>
                  </div>
                  <div className="tpl-item-actions">
                    <IconButton label="Редактировать" onClick={(e) => { e.stopPropagation(); setEditingId(t.id) }}>
                      <Pencil size={14} />
                    </IconButton>
                    <IconButton label="Удалить" tone="danger" onClick={(e) => { e.stopPropagation(); removeTemplate(t) }}>
                      <Trash2 size={14} />
                    </IconButton>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </section>
    </div>
  )
}

// Инлайн-редактор сохранённого шаблона.
function TemplateEditor({
  template,
  onSave,
  onCancel
}: {
  template: ReplyTemplate
  onSave: (template: ReplyTemplate, title: string, body: string) => void
  onCancel: () => void
}) {
  const [title, setTitle] = useState(template.title)
  const [body, setBody] = useState(template.body)
  return (
    <div className="tpl-edit">
      <Input value={title} placeholder="Название" onChange={(e) => setTitle(e.target.value)} autoFocus />
      <Textarea value={body} rows={3} placeholder="Текст шаблона…" onChange={(e) => setBody(e.target.value)} />
      <div className="tpl-edit-actions">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Отмена
        </Button>
        <Button size="sm" onClick={() => onSave(template, title, body)} disabled={!body.trim()}>
          Сохранить
        </Button>
      </div>
    </div>
  )
}
